import { Controller } from "@hotwired/stimulus"

// Estimates search time and results based on coverage options
export default class extends Controller {
  static targets = ["gridSearch", "thoroughness", "maxPages", "estimate", "summary", "details", "gridOptions"]

  // Estimated grid cells for a 10km radius search
  static cellEstimates = {
    quick: { cells: 5, cellSize: "5km" },
    standard: { cells: 13, cellSize: "3km" },
    thorough: { cells: 29, cellSize: "2km" },
    exhaustive: { cells: 69, cellSize: "1.5km" }
  }

  // Cost per grid cell (matches Ruby model constant)
  // Formula: 2.1 cells/initial × 3 calls × $0.032 = $0.20
  static costPerCell = 0.20

  connect() {
    this.update()
  }

  update() {
    const gridSearch = this.gridSearchTarget.checked
    const thoroughness = this.thoroughnessTarget.value
    const maxPages = parseInt(this.maxPagesTarget.value)

    // Show/hide grid options and estimate based on checkbox
    if (this.hasGridOptionsTarget) {
      this.gridOptionsTarget.style.display = gridSearch ? "grid" : "none"
    }
    if (this.hasEstimateTarget) {
      this.estimateTarget.style.display = gridSearch ? "block" : "none"
    }

    if (!gridSearch) {
      return
    }

    const estimate = this.constructor.cellEstimates[thoroughness] || this.constructor.cellEstimates.standard
    const resultsPerCell = maxPages * 20
    const totalResults = estimate.cells * resultsPerCell
    const timeSeconds = estimate.cells * 2 // ~2 seconds per cell (API call + processing)

    let timeDisplay
    if (timeSeconds < 60) {
      timeDisplay = `~${timeSeconds} seconds`
    } else {
      const minutes = Math.ceil(timeSeconds / 60)
      timeDisplay = `~${minutes} minute${minutes > 1 ? 's' : ''}`
    }

    const thoroughnessLabels = {
      quick: "Quick search",
      standard: "Standard search",
      thorough: "Thorough search",
      exhaustive: "Exhaustive search"
    }

    this.summaryTarget.textContent = `${thoroughnessLabels[thoroughness]}: ${timeDisplay}`
    this.detailsTarget.textContent = `~${estimate.cells} grid cells (${estimate.cellSize} each), up to ${totalResults.toLocaleString()} results`

    // Auto-calculate and set budget for grid searches
    this.updateBudgetEstimate(estimate.cells)
  }

  updateBudgetEstimate(cellCount) {
    // Find budget elements (they're outside this controller's scope)
    const form = this.element.closest('form')
    if (!form) return

    const budgetCheckbox = form.querySelector('input[name="companies_search[budget_enabled]"]')
    const budgetInput = form.querySelector('input[name="companies_search[budget_dollars]"]')

    if (!budgetInput) return

    // Only auto-fill if this is a new value (input is empty or was auto-set)
    const isUserSet = budgetInput.dataset.userSet === 'true'

    if (cellCount > 0 && !isUserSet) {
      // Calculate estimated budget
      const estimatedCost = cellCount * this.constructor.costPerCell
      // Round up to nearest dollar for simplicity
      const roundedBudget = Math.max(1, Math.ceil(estimatedCost))
      budgetInput.value = roundedBudget.toFixed(2)
      budgetInput.dataset.autoSet = 'true'

      // Auto-enable budget checkbox
      if (budgetCheckbox && !budgetCheckbox.checked) {
        budgetCheckbox.checked = true
        // Trigger the toggle to show the budget fields
        budgetCheckbox.dispatchEvent(new Event('change', { bubbles: true }))
      }
    }

    // Track if user manually changes the budget
    if (!budgetInput.dataset.listenerAdded) {
      budgetInput.addEventListener('input', () => {
        budgetInput.dataset.userSet = 'true'
        budgetInput.dataset.autoSet = 'false'
      })
      budgetInput.dataset.listenerAdded = 'true'
    }
  }
}
